import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getUserLanguagePreference, updateUserLanguagePreference } from '../services/userService';
import { getUserFacility, getFacilityGroup } from '../services/facilityService';
import { localeConfig } from '../i18n';
import { CircularProgress, Box } from '@mui/material';
import { format } from 'date-fns';

const LocaleContext = createContext();

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

export const LocaleProvider = ({ children }) => {
  const { i18n, t: i18nT, ready } = useTranslation();
  const [currentLocale, setCurrentLocale] = useState('en');
  const [userFacilityGroup, setUserFacilityGroup] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const user = useSelector((state) => state.auth.user);

  // Enhanced translation function that checks if i18next is ready
  const t = (key, params = {}) => {
    if (!ready || !i18n.isInitialized) {
      // Return the key itself as fallback when i18next is not ready
      return key;
    }
    return i18nT(key, params);
  };

  // Get user's profile and facility group to set locale
  useEffect(() => {
    const getUserData = async () => {
      if (!user) {
        setIsInitialized(true);
        return;
      }

      try {
        // Use service layer instead of direct Firestore access
        let userData = null;
        let userFacility = null;
        let facilityGroup = null;

        try {
          // Get user's personal language preference
          userData = await getUserLanguagePreference();
          setUserProfile(userData);
          
          // If user has a personal language preference, use it
          if (userData && userData.languagePreference) {
            setCurrentLocale(userData.languagePreference);
            if (i18n.isInitialized) {
              i18n.changeLanguage(userData.languagePreference);
            }
            setIsInitialized(true);
            return; // Don't check facility group if user has personal preference
          }
        } catch (error) {
          console.warn('Could not fetch user language preference:', error);
        }

        try {
          // If no personal preference, get user's facility and set locale from facility group
          userFacility = await getUserFacility(user.uid);
          
          if (userFacility && userFacility.facilityId) {
            facilityGroup = await getFacilityGroup(userFacility.facilityId);
            
            if (facilityGroup) {
              setUserFacilityGroup(facilityGroup);
              // Set locale from facility group
              const groupLocale = facilityGroup.locale || 'en';
              setCurrentLocale(groupLocale);
              if (i18n.isInitialized) {
                i18n.changeLanguage(groupLocale);
              }
            }
          }
        } catch (error) {
          console.warn('Could not fetch user facility or facility group:', error);
        }

        // If no preference found, use default locale
        if (!userData?.languagePreference && !facilityGroup?.locale) {
          setCurrentLocale('en');
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Error getting user data:', error);
        // Fallback to default locale
        setCurrentLocale('en');
        setIsInitialized(true);
      }
    };

    getUserData();
  }, [user, i18n]);

  // Update user's personal language preference when locale changes
  const updateUserLanguagePreference = async (newLocale) => {
    if (!user) return;
    
    try {
      // Use service layer instead of direct Firestore access
              await updateUserLanguagePreference(newLocale);
      
      // Update local state
      setUserProfile(prev => prev ? { ...prev, languagePreference: newLocale } : null);
    } catch (error) {
      console.error('Error updating user language preference:', error);
    }
  };

  // Enhanced setCurrentLocale that also updates user preference and i18n
  const setCurrentLocaleWithUserUpdate = (newLocale) => {
    setCurrentLocale(newLocale);
    if (i18n.isInitialized) {
      i18n.changeLanguage(newLocale);
    }
    updateUserLanguagePreference(newLocale);
  };

  // Format number according to locale
  const formatNumber = (number, options = {}) => {
    const config = localeConfig[currentLocale];
    const { precision } = config.numberFormat;
    
    return new Intl.NumberFormat(currentLocale, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
      ...options
    }).format(number);
  };

  // Format currency according to locale
  const formatCurrency = (amount, currency = null) => {
    const config = localeConfig[currentLocale];
    const currencyCode = currency || config.currency;
    
    return new Intl.NumberFormat(currentLocale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: config.numberFormat.precision,
      maximumFractionDigits: config.numberFormat.precision,
    }).format(amount);
  };

  // Format date according to locale
  const formatDate = (date, format = null) => {
    const config = localeConfig[currentLocale];
    const dateFormat = format || config.dateFormat;
    
    // Simple date formatting - you might want to use a library like date-fns
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    
    if (dateFormat === 'dd/MM/yyyy') {
      return `${day}/${month}/${year}`;
    }
    return `${month}/${day}/${year}`;
  };

  // Format date and time according to locale
  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return format(dateObj, 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const value = {
    currentLocale: currentLocale || 'en',
    setCurrentLocale: setCurrentLocaleWithUserUpdate,
    userFacilityGroup,
    userProfile,
    localeConfig: localeConfig,
    t,
    formatNumber,
    formatCurrency,
    formatDate,
    formatDateTime,
    isRTL: (localeConfig[currentLocale] || localeConfig.en)?.direction === 'rtl',
    isInitialized: isInitialized && ready && i18n.isInitialized,
    // Expose i18n instance for advanced usage
    i18n
  };

  // Show loading spinner until both i18next and user data are initialized
  if (!isInitialized || !ready || !i18n.isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}; 